// SHARED FOUNDATION — owned by the page skeleton; FROZEN for tab agents.
// The single wiring point between the customer-page tab strip and the tab
// modules. Tab agents implement their own tab FILE (per the ownership comment
// at its top) — they do NOT edit this registry. Order here == visual order of
// the tab strip (RTL: first item renders rightmost).
import OverviewTab, { useOverviewTabBadge, preloadOverviewTab } from "./OverviewTab";
import TimelineTab, { useTimelineTabBadge, preloadTimelineTab } from "./TimelineTab";
import MatchesTab, { useMatchesTabBadge, preloadMatchesTab } from "./MatchesTab";
import CallsTab, { useCallsTabBadge, preloadCallsTab } from "./CallsTab";
import TasksTab, { useTasksTabBadge, preloadTasksTab } from "./TasksTab";
import MeetingsTab, { useMeetingsTabBadge, preloadMeetingsTab } from "./MeetingsTab";
import SignaturesTab, { useSignaturesTabBadge, preloadSignaturesTab } from "./SignaturesTab";
import NotesTab, { useNotesTabBadge, preloadNotesTab } from "./NotesTab";
import DocumentsTab, { useDocumentsTabBadge, preloadDocumentsTab } from "./DocumentsTab";
import BroadcastTab, { useBroadcastTabBadge, preloadBroadcastTab } from "./BroadcastTab";
import { contactShowsBuyerSurfaces } from "../../../../../utils/contacts/contactSides";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

export const CONTACT_DETAIL_TAB_IDS = [
  "overview",
  "timeline",
  "matches",
  "calls",
  "tasks",
  "meetings",
  "signatures",
  "notes",
  "documents",
  "broadcast"
] as const;

export type ContactDetailTabId = (typeof CONTACT_DETAIL_TAB_IDS)[number];

export function isContactDetailTabId(value: unknown): value is ContactDetailTabId {
  return (
    typeof value === "string" && (CONTACT_DETAIL_TAB_IDS as readonly string[]).includes(value)
  );
}

export type ContactDetailTabDefinition = {
  id: ContactDetailTabId;
  /** Hebrew tab label (inline by convention — no locales edits for the CRM revamp). */
  label: string;
  Component: (props: ContactTabComponentProps) => JSX.Element;
  /** React hook — called by the tab strip on every render (see tabContract.ts). */
  useBadge: (contact: RealEstateContact) => TabBadge;
  /** Fire-and-forget cache warm-up; must never throw. */
  preload: (contact: RealEstateContact) => void;
  /**
   * Per-contact visibility predicate — omit for always-visible (the default).
   * Plain function, NOT a hook (must be callable from non-React code like
   * preloadAllContactTabs); derive visibility from the contact doc only.
   * A hidden tab is fully absent: no strip entry, no panel, no preload.
   */
  isVisible?: (contact: RealEstateContact) => boolean;
};

// התאמות (matches) is a buyer-side feature for now, so a PURE seller-side contact
// doesn't get the tab at all — the rule itself (and why a mixed-type or untyped
// contact still keeps it) lives in `utils/contacts/contactSides.ts`, shared with the
// CRM row's matches indicator and the סקירה tab's ✦ פרטי חיפוש panel. Seller-side
// replacement TBD ("something else later").
//
// Wrapped rather than used directly because `isVisible` is a per-TAB predicate on the
// definition below: the indirection is what names which tab this decision is about.
function isMatchesTabVisible(contact: RealEstateContact): boolean {
  return contactShowsBuyerSurfaces(contact);
}

export const CONTACT_DETAIL_TABS: readonly ContactDetailTabDefinition[] = [
  { id: "overview", label: "סקירה", Component: OverviewTab, useBadge: useOverviewTabBadge, preload: preloadOverviewTab },
  { id: "timeline", label: "ציר זמן", Component: TimelineTab, useBadge: useTimelineTabBadge, preload: preloadTimelineTab },
  { id: "matches", label: "התאמות", Component: MatchesTab, useBadge: useMatchesTabBadge, preload: preloadMatchesTab, isVisible: isMatchesTabVisible },
  { id: "calls", label: "שיחות", Component: CallsTab, useBadge: useCallsTabBadge, preload: preloadCallsTab },
  { id: "tasks", label: "משימות", Component: TasksTab, useBadge: useTasksTabBadge, preload: preloadTasksTab },
  { id: "meetings", label: "פגישות", Component: MeetingsTab, useBadge: useMeetingsTabBadge, preload: preloadMeetingsTab },
  { id: "signatures", label: "חתימות", Component: SignaturesTab, useBadge: useSignaturesTabBadge, preload: preloadSignaturesTab },
  { id: "notes", label: "הערות", Component: NotesTab, useBadge: useNotesTabBadge, preload: preloadNotesTab },
  { id: "documents", label: "מסמכים", Component: DocumentsTab, useBadge: useDocumentsTabBadge, preload: preloadDocumentsTab },
  { id: "broadcast", label: "דיוור", Component: BroadcastTab, useBadge: useBroadcastTabBadge, preload: preloadBroadcastTab }
];

/**
 * The tab definitions visible for THIS contact, in strip order. The single
 * chokepoint for per-contact tab visibility — the strip, the panels, and the
 * preloader must all go through it so a hidden tab is consistently absent.
 */
export function getVisibleContactDetailTabs(
  contact: RealEstateContact
): readonly ContactDetailTabDefinition[] {
  return CONTACT_DETAIL_TABS.filter((tab) => tab.isVisible?.(contact) ?? true);
}

/**
 * Runs every VISIBLE tab's preload for the contact, error-safe. Reached via the
 * dynamic import in sessionCache.preloadContactDetail() so the contacts table
 * can warm the page without statically bundling the tab code into its chunk.
 * Hidden tabs are skipped — no point warming data the contact can never open.
 */
export function preloadAllContactTabs(contact: RealEstateContact): void {
  getVisibleContactDetailTabs(contact).forEach((tab) => {
    try {
      tab.preload(contact);
    } catch {
      // preloads are best-effort by contract — a broken one must not block the rest
    }
  });
}
